import React from "react";
import { Helmet } from "react-helmet";
import Context from "../../context/conventionTrackerContext";
import { cloneDeep } from "lodash";
import { Background, ProductCard, Ticker, SocialBar } from "../../components";
import { productSort } from "../../utils";
import { COPY, BRAND, GA_ID } from "../../utils/constants";
import {
  useDisplayProducts,
  useHandleWishlist,
  useListenSockets,
  useConventionView,
  useConventionState,
  useFetchConventionData,
  useHandlers,
} from "./hooks";
import "./index.scss";
import { useSelector } from "react-redux";
import { isLoggedInSelector } from "../../store/authSlice";
import { getSelfServiceCnameSelector } from "../../store/selfServiceSlice";
import { getConventionsSelector } from "../../store/conventionsSlice";

/**
 * @desc Main entry point of the app.
 */
const ConventionTracker = ({ brand, isPreview = false }) => {
  // CONSTANTS ----------------------------------------------------------------

  const isLoungeFly = brand === BRAND.LOUNGEFLY;
  const { convention } = useFetchConventionData(brand);
  const previewCname = useSelector(getSelfServiceCnameSelector);
  const conventions = useSelector(getConventionsSelector);
  const isLoggedIn = useSelector(isLoggedInSelector);
  const previewConvention = conventions.find(
    (item) => item.name === previewCname
  );
  const targetConvention = isPreview ? previewConvention : convention;

  // COMPONENT STATE ----------------------------------------------------------

  const { products, setProducts } = useConventionState(targetConvention);

  // COMPONENT CONSTANTS ------------------------------------------------------

  const cname = targetConvention?.name || "";
  const isLive = !!cname;
  const isDarkMode = !!targetConvention?.darkMode;
  const gtag = GA_ID[brand];

  // SIDE EFFECTS -------------------------------------------------------------

  useConventionView({ cname: isPreview ? previewCname : cname });

  const { wid, wishlist, setWishlist, setShowWishlist } = useHandleWishlist({
    brand,
    cname,
    isLive,
  });

  useListenSockets({
    cname: isPreview ? previewCname : cname,
    isLive: isPreview ? true : isLive,
    brand,
    products,
  });

  const displayProducts = useDisplayProducts({
    products,
    wishlist,
  });

  // HANDLERS -----------------------------------------------------------------

  const { onTabClick, onFavoriteClick } = useHandlers({
    setShowWishlist,
    setWishlist,
    wid,
    wishlist,
  });

  // COMPONENT ----------------------------------------------------------------

  /**
   * @desc Renders Product Cards.
   */
  const renderMain = () => {
    if (Array.isArray(displayProducts) && displayProducts.length) {
      const sortedProducts = cloneDeep(displayProducts).sort(productSort);
      return sortedProducts.map((product) => {
        return <ProductCard {...product} key={product.sku} />;
      });
    }
    return (
      <div className="empty-message">
        {wishlist.show ? COPY.EMPTY_WISHLIST : COPY.EMPTY_CONLIST}
      </div>
    );
  };

  // METHODS ------------------------------------------------------------------

  const getClassName = () => {
    let className = `app ${brand}`;
    if (isDarkMode) {
      className += " dark-mode";
    }
    return className;
  };

  // MAIN ---------------------------------------------------------------------

  if (!targetConvention) {
    return "Convention does not exists.";
  }

  if (!isLive && !isPreview) {
    return "Convention is not live.";
  }

  if (isPreview && !isLoggedIn) {
    return "You need to be logged in to preview.";
  }

  const contextValue = {
    convention: targetConvention,
    isLoungeFly,
    brand,
    wid,
    onFavoriteClick,
    wishlist,
    onTabClick,
    isDarkMode,
    setProducts,
  };

  return (
    <Context.Provider value={contextValue}>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
        <link
          href="https://fonts.googleapis.com/css2?family=Exo:ital,wght@0,100..900;1,100..900&family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"
          rel="stylesheet"
        />
        <script
          key="GA-1"
          async
          src={`https://www.googletagmanager.com/gtag/js?id=${gtag}`}
        ></script>
        <script key="GA-2">{`window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${gtag}');`}</script>
      </Helmet>
      <div className={getClassName()}>
        <Background>{renderMain()}</Background>
        <Ticker />
        <SocialBar />
      </div>
    </Context.Provider>
  );
};

export default ConventionTracker;
